// src/components/pages/EventManager/organisms/ContentList.js

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  LinearProgress,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  collection,
  onSnapshot,
  doc,
  deleteDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, auth } from "../../../../firebase";
import CustomButton from "../atoms/CustomButton";
import InputField from "../atoms/InputField";

const storage = getStorage();

const ContentList = ({ theme, event }) => {
  const [contentItems, setContentItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  // Manual Asset Dialog state
  const [manualOpen, setManualOpen] = useState(false);
  const [mTitle, setMTitle] = useState("");
  const [mDescription, setMDescription] = useState("");
  const [mCreator, setMCreator] = useState("");

  // Files & progress
  const [fullVideoFile, setFullVideoFile] = useState(null);
  const [teaserVideoFile, setTeaserVideoFile] = useState(null); // OPTIONAL
  const [imageFile, setImageFile] = useState(null);

  // Per-file progress/error (LinearProgress = bars)
  const [progressFull, setProgressFull] = useState(0);
  const [progressTeaser, setProgressTeaser] = useState(0);
  const [progressImage, setProgressImage] = useState(0);
  const [errorFull, setErrorFull] = useState("");
  const [errorTeaser, setErrorTeaser] = useState("");
  const [errorImage, setErrorImage] = useState("");

  const [uploading, setUploading] = useState(false);

  // Remove confirmation
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  // input refs for reliable "Choose File"
  const fullInputRef = useRef(null);
  const teaserInputRef = useRef(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    if (!event?.id) return;
    const contentRef = collection(db, "events", event.id, "content");
    const un = onSnapshot(contentRef, (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setContentItems(items);
    });
    return () => un();
  }, [event]);

  const allSelected = useMemo(
    () => contentItems.length > 0 && selected.length === contentItems.length,
    [contentItems, selected]
  );

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (allSelected) setSelected([]);
    else setSelected(contentItems.map((i) => i.id));
  };

  const openSnackbar = (msg, severity = "success") =>
    setSnackbar({ open: true, msg, severity });
  const closeSnackbar = () =>
    setSnackbar((s) => ({ ...s, open: false }));

  // ---------- Manual Asset Dialog ----------
  const resetManualForm = () => {
    setMTitle("");
    setMDescription("");
    setMCreator("");
    setFullVideoFile(null);
    setTeaserVideoFile(null);
    setImageFile(null);
    setUploading(false);
    setProgressFull(0);
    setProgressTeaser(0);
    setProgressImage(0);
    setErrorFull("");
    setErrorTeaser("");
    setErrorImage("");
  };

  const handleOpenManual = () => {
    resetManualForm();
    setManualOpen(true);
  };
  const handleCloseManual = () => {
    if (uploading) return;
    setManualOpen(false);
  };

  const fileInfo = (f) =>
    f ? `${f.name} (${Math.round(f.size / 1024 / 1024)} MB)` : "None selected";

  const validVideo = (f) => !!f && /^video\//.test(f.type);
  const validImage = (f) => !!f && /^image\//.test(f.type);

  const ext = (name) => {
    const idx = name.lastIndexOf(".");
    return idx >= 0 ? name.substring(idx) : "";
  };

  // Reliable "Choose File" triggers
  const pickFull = () => fullInputRef.current?.click();
  const pickTeaser = () => teaserInputRef.current?.click();
  const pickImage = () => imageInputRef.current?.click();

  const onFullChange = (e) => {
    const f = e.target.files?.[0] || null;
    setErrorFull("");
    setProgressFull(0);
    if (f && !validVideo(f)) {
      setErrorFull("Please select a valid video file.");
      setFullVideoFile(null);
      return;
    }
    setFullVideoFile(f);
  };
  const onTeaserChange = (e) => {
    const f = e.target.files?.[0] || null;
    setErrorTeaser("");
    setProgressTeaser(0);
    if (f && !validVideo(f)) {
      setErrorTeaser("Please select a valid video file.");
      setTeaserVideoFile(null);
      return;
    }
    setTeaserVideoFile(f);
  };
  const onImageChange = (e) => {
    const f = e.target.files?.[0] || null;
    setErrorImage("");
    setProgressImage(0);
    if (f && !validImage(f)) {
      setErrorImage("Please select a valid image file.");
      setImageFile(null);
      return;
    }
    setImageFile(f);
  };

  const clearFull = () => {
    setFullVideoFile(null);
    setErrorFull("");
    setProgressFull(0);
    if (fullInputRef.current) fullInputRef.current.value = "";
  };
  const clearTeaser = () => {
    setTeaserVideoFile(null);
    setErrorTeaser("");
    setProgressTeaser(0);
    if (teaserInputRef.current) teaserInputRef.current.value = "";
  };
  const clearImage = () => {
    setImageFile(null);
    setErrorImage("");
    setProgressImage(0);
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  // Retry helpers
  const delay = (ms) => new Promise((res) => setTimeout(res, ms));

  const isRetryableStorageError = (err) => {
    const code = err?.code || "";
    if (!code) return true;
    return (
      code === "storage/retry-limit-exceeded" ||
      code === "storage/unknown" ||
      code === "storage/quota-exceeded" ||
      code === "storage/unauthenticated"
    );
  };

  async function uploadWithRetry(storagePath, file, onProgress, setError, maxRetries = 2) {
    let attempt = 0;
    let lastErr = null;
    while (attempt <= maxRetries) {
      try {
        const result = await new Promise((resolve, reject) => {
          const task = uploadBytesResumable(ref(storage, storagePath), file, {
            contentType: file.type,
          });
          task.on(
            "state_changed",
            (snap) => {
              const pct = Math.round(
                (snap.bytesTransferred / snap.totalBytes) * 100
              );
              onProgress?.(pct);
            },
            (err) => reject(err),
            async () => {
              try {
                const url = await getDownloadURL(task.snapshot.ref);
                resolve({
                  url,
                  path: storagePath,
                  contentType: file.type,
                  size: file.size,
                  updatedAt: Date.now(),
                });
              } catch (e) {
                reject(e);
              }
            }
          );
        });
        setError("");
        return result;
      } catch (err) {
        lastErr = err;
        if (attempt === maxRetries || !isRetryableStorageError(err)) {
          setError(
            `Upload failed${attempt ? ` after ${attempt + 1} attempts` : ""}. ${
              err?.message || "Unknown error"
            }`
          );
          break;
        }
        const backoff = 500 + attempt * 1000;
        await delay(backoff);
        attempt += 1;
      }
    }
    throw lastErr;
  }

  const removeStorageIfManual = async (item) => {
    if (!item?.isManual) return;
    const candidates = [
      item.fullVideo?.path,
      item.teaserVideo?.path,
      item.thumbnail?.path,
    ].filter(Boolean);
    await Promise.all(
      candidates.map(async (p) => {
        try {
          await deleteObject(ref(storage, p));
        } catch (e) {
          console.warn("[Remove] could not delete storage object:", p, e?.code || e?.message);
        }
      })
    );
  };

  const handleCloseRemoveConfirm = () => {
    if (removing) return;
    setConfirmOpen(false);
  };
  const handleRemoveSelected = async () => {
    if (!event?.id || selected.length === 0) return;
    setRemoving(true);
    try {
      const byId = contentItems.reduce((acc, it) => ({ ...acc, [it.id]: it }), {});
      await Promise.all(
        selected.map(async (id) => {
          const item = byId[id];
          if (!item) return;
          await removeStorageIfManual(item);
          await deleteDoc(doc(db, "events", event.id, "content", id));
        })
      );
      setSelected([]);
      openSnackbar("Removed selected content.");
      setConfirmOpen(false);
    } catch (err) {
      console.error("[Remove] error:", err);
      openSnackbar("Failed to remove some items.", "error");
    } finally {
      setRemoving(false);
    }
  };

  const handleSaveManual = async () => {
    // Basic validations
    if (!mTitle.trim()) return openSnackbar("Please add a title.", "error");
    if (!mDescription.trim())
      return openSnackbar("Please add a description.", "error");
    if (!mCreator.trim())
      return openSnackbar("Please add a creator.", "error");
    if (!validVideo(fullVideoFile))
      return openSnackbar("Full video required (video/*).", "error");
    // teaserVideoFile is OPTIONAL:
    if (teaserVideoFile && !validVideo(teaserVideoFile))
      return openSnackbar("Teaser must be a valid video file.", "error");
    if (!validImage(imageFile))
      return openSnackbar("Thumbnail image required (image/*).", "error");

    const user = auth.currentUser;
    if (!user) return openSnackbar("No authenticated user.", "error");

    setUploading(true);
    setErrorFull("");
    setErrorTeaser("");
    setErrorImage("");

    try {
      // Pre-generate doc id for stable storage paths
      const contentCol = collection(db, "events", event.id, "content");
      const contentDocRef = doc(contentCol);
      const dir = `events/${event.id}/content/${contentDocRef.id}`;

      // Uploads: full + image required, teaser optional
      const promises = [
        uploadWithRetry(`${dir}/full${ext(fullVideoFile.name)}`, fullVideoFile, setProgressFull, setErrorFull, 2),
        uploadWithRetry(`${dir}/thumb${ext(imageFile.name)}`, imageFile, setProgressImage, setErrorImage, 2),
        teaserVideoFile
          ? uploadWithRetry(`${dir}/teaser${ext(teaserVideoFile.name)}`, teaserVideoFile, setProgressTeaser, setErrorTeaser, 2)
          : Promise.resolve(null),
      ];
      const [fullMeta, imageMeta, teaserMeta] = await Promise.all(promises);

      // Write Firestore doc (conditionally include teaserVideo)
      await setDoc(contentDocRef, {
        isManual: true,
        title: mTitle.trim(),
        description: mDescription.trim(),
        creator: mCreator.trim(),
        fullVideo: fullMeta,
        ...(teaserMeta ? { teaserVideo: teaserMeta } : {}),
        thumbnail: imageMeta,
        createdAt: serverTimestamp(),
        createdByUid: user.uid,
      });

      openSnackbar("Manual asset added.");
      setManualOpen(false);
      resetManualForm();
    } catch (err) {
      console.error("[ManualAsset] upload/save error:", err);
      openSnackbar("Failed to add manual asset. Fix errors and try again.", "error");
      // Keep dialog open so the user can retry a failed file or remove teaser to proceed.
    } finally {
      setUploading(false);
    }
  };

  // Single-file retry (for user feedback before pressing Save again)
  const retryFull = async () => {
    if (!validVideo(fullVideoFile)) return;
    setErrorFull("");
    try {
      setUploading(true);
      const dir = `events/_tmp`;
      await uploadWithRetry(
        `${dir}/full-retry${ext(fullVideoFile.name)}`,
        fullVideoFile,
        setProgressFull,
        setErrorFull,
        2
      );
      openSnackbar("Full video looks good—try Save again.", "success");
    } catch (e) {
      /* errorFull set by uploader */
    } finally {
      setUploading(false);
    }
  };
  const retryTeaser = async () => {
    if (!teaserVideoFile || !validVideo(teaserVideoFile)) return;
    setErrorTeaser("");
    try {
      setUploading(true);
      const dir = `events/_tmp`;
      await uploadWithRetry(
        `${dir}/teaser-retry${ext(teaserVideoFile.name)}`,
        teaserVideoFile,
        setProgressTeaser,
        setErrorTeaser,
        2
      );
      openSnackbar("Teaser video looks good—try Save again.", "success");
    } catch (e) {
      /* errorTeaser set */
    } finally {
      setUploading(false);
    }
  };
  const retryImage = async () => {
    if (!validImage(imageFile)) return;
    setErrorImage("");
    try {
      setUploading(true);
      const dir = `events/_tmp`;
      await uploadWithRetry(
        `${dir}/thumb-retry${ext(imageFile.name)}`,
        imageFile,
        setProgressImage,
        setErrorImage,
        2
      );
      openSnackbar("Thumbnail looks good—try Save again.", "success");
    } catch (e) {
      /* errorImage set */
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      {/* Header and Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontFamily: "basic-sans", fontWeight: 700, color: "white" }}
        >
          Current Content
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <CustomButton
            theme={theme}
            onClick={handleSelectAll}
            sx={{
              backgroundColor: theme.colors.mediumPurple,
              "&:hover": { backgroundColor: theme.colors.blue },
            }}
          >
            {allSelected ? "Clear Selection" : "Select All"}
          </CustomButton>

          <CustomButton
            theme={theme}
            onClick={handleOpenManual}
            sx={{
              backgroundColor: theme.colors.darkBlue,
              "&:hover": { backgroundColor: theme.colors.amber },
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <AddCircleOutlineIcon fontSize="small" />
            Add Manual Asset
          </CustomButton>

          <CustomButton
            theme={theme}
            onClick={() => setConfirmOpen(true)}
            disabled={selected.length === 0}
            sx={{
              backgroundColor:
                selected.length === 0
                  ? `${theme.colors.palePurple} !important`
                  : theme.colors.mediumPurple,
              color:
                selected.length === 0
                  ? `${theme.colors.darkPurple} !important`
                  : "white",
              "&:hover": {
                backgroundColor:
                  selected.length === 0 ? theme.colors.gray : theme.colors.red,
              },
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: selected.length === 0 ? "not-allowed" : "pointer",
            }}
          >
            <RemoveCircleOutlineIcon fontSize="small" />
            Remove
          </CustomButton>
        </Box>
      </Box>

      {/* Grid of content */}
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {contentItems.length === 0 ? (
          <Grid item xs={12}>
            <Typography sx={{ color: "white", fontFamily: "basic-sans" }}>
              No content yet. Add a manual asset or select from a Challenge below.
            </Typography>
          </Grid>
        ) : (
          contentItems.map((item) => {
            const fromChallenge = !item.isManual && !!item.submissionSaleId;
            const manualThumb = item.thumbnail?.url;
            const challengeThumb = fromChallenge
              ? `https://swayer.blob.core.windows.net/${item.submissionSaleId}-pub/_thumb_large.jpg`
              : null;

            const thumbnailUrl = manualThumb || challengeThumb || null;

            return (
              <Grid item key={item.id} xs={6} sm={3} md={4} lg={2} sx={{ display: "flex" }}>
                <Card
                  onClick={() => toggleSelect(item.id)}
                  sx={{
                    backgroundColor: theme.colors.mediumPurple,
                    color: "white",
                    fontFamily: "basic-sans",
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "20px",
                    border: selected.includes(item.id)
                      ? `2px solid ${theme.colors.red}`
                      : `2px solid ${theme.colors.darkBlue}`,
                    cursor: "pointer",
                  }}
                >
                  {thumbnailUrl && (
                    <CardMedia
                      component="div"
                      sx={{
                        paddingTop: "56.25%",
                        backgroundImage: `url(${thumbnailUrl})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                      alt={item.title || "Content Thumbnail"}
                    />
                  )}
                  <CardContent>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "basic-sans", color: "white", fontWeight: 700 }}
                    >
                      {item.title || item.id}
                    </Typography>
                    {item.isManual && (
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Manual asset · {item.creator || "Unknown"}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })
        )}
      </Grid>

      {/* Manual Asset Dialog */}
      <Dialog
        open={manualOpen}
        onClose={handleCloseManual}
        fullWidth
        maxWidth="sm"
        aria-labelledby="manual-asset-title"
        PaperProps={{
          sx: {
            backgroundColor: theme.colors.mediumPurple,
            color: "white",
            fontFamily: "basic-sans",
            border: `1px solid ${theme.colors.palePurple}`,
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          id="manual-asset-title"
          sx={{
            bgcolor: theme.colors.headerPurple,
            color: theme.colors.amber,
            fontWeight: 700,
            borderBottom: `1px solid ${theme.colors.palePurple}`,
            pr: 6,
          }}
        >
          Add Manual Asset
          <IconButton
            aria-label="close"
            onClick={handleCloseManual}
            size="small"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.colors.amber,
              "&:hover": { color: theme.colors.green },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ bgcolor: theme.colors.mediumPurple, borderColor: theme.colors.palePurple }}
        >
          <Stack spacing={2}>
            <InputField
              label="Title"
              type="text"
              value={mTitle}
              onChange={(e) => setMTitle(e.target.value)}
              required
            />
            <InputField
              label="Description"
              type="textarea"
              value={mDescription}
              onChange={(e) => setMDescription(e.target.value)}
              required
            />
            <InputField
              label="Creator"
              type="text"
              value={mCreator}
              onChange={(e) => setMCreator(e.target.value)}
              required
            />

            {/* Full + Teaser pickers side-by-side (teaser is OPTIONAL) */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              {/* FULL VIDEO (required) */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Full Video (required)
                </Typography>

                <input
                  ref={fullInputRef}
                  type="file"
                  accept="video/*"
                  style={{ display: "none" }}
                  onChange={onFullChange}
                />

                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <CustomButton
                    onClick={pickFull}
                    theme={theme}
                    sx={{
                      backgroundColor: theme.colors.darkBlue,
                      "&:hover": { backgroundColor: theme.colors.amber },
                    }}
                  >
                    Choose File
                  </CustomButton>
                  {fullVideoFile && (
                    <IconButton
                      aria-label="clear full"
                      onClick={clearFull}
                      size="small"
                      sx={{ color: theme.colors.amber }}
                    >
                      <HighlightOffIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                  {fileInfo(fullVideoFile)}
                </Typography>

                {uploading && (
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress variant="determinate" value={progressFull} />
                    <Typography variant="caption">{progressFull}%</Typography>
                  </Box>
                )}

                {errorFull && (
                  <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="caption" color="error">
                      {errorFull}
                    </Typography>
                    {fullVideoFile && (
                      <Button
                        size="small"
                        startIcon={<ReplayIcon />}
                        onClick={retryFull}
                        disabled={uploading}
                        sx={{ textTransform: "none" }}
                      >
                        Retry this file
                      </Button>
                    )}
                  </Box>
                )}
              </Box>

              {/* TEASER VIDEO (optional) */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Teaser Video (optional)
                </Typography>

                <input
                  ref={teaserInputRef}
                  type="file"
                  accept="video/*"
                  style={{ display: "none" }}
                  onChange={onTeaserChange}
                />

                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <CustomButton
                    onClick={pickTeaser}
                    theme={theme}
                    sx={{
                      backgroundColor: theme.colors.darkBlue,
                      "&:hover": { backgroundColor: theme.colors.amber },
                    }}
                  >
                    Choose File
                  </CustomButton>
                  {teaserVideoFile && (
                    <IconButton
                      aria-label="clear teaser"
                      onClick={clearTeaser}
                      size="small"
                      sx={{ color: theme.colors.amber }}
                    >
                      <HighlightOffIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                  {fileInfo(teaserVideoFile)}
                </Typography>

                {uploading && teaserVideoFile && (
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress variant="determinate" value={progressTeaser} />
                    <Typography variant="caption">{progressTeaser}%</Typography>
                  </Box>
                )}

                {errorTeaser && teaserVideoFile && (
                  <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="caption" color="error">
                      {errorTeaser}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<ReplayIcon />}
                      onClick={retryTeaser}
                      disabled={uploading}
                      sx={{ textTransform: "none" }}
                    >
                      Retry this file
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>

            {/* THUMBNAIL IMAGE (required) */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Thumbnail Image (required)
              </Typography>

              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={onImageChange}
              />

              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <CustomButton
                  onClick={pickImage}
                  theme={theme}
                  sx={{
                    backgroundColor: theme.colors.darkBlue,
                    "&:hover": { backgroundColor: theme.colors.amber },
                  }}
                >
                  Choose File
                </CustomButton>
                {imageFile && (
                  <IconButton
                    aria-label="clear image"
                    onClick={clearImage}
                    size="small"
                    sx={{ color: theme.colors.amber }}
                  >
                    <HighlightOffIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                {fileInfo(imageFile)}
              </Typography>

              {uploading && (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress variant="determinate" value={progressImage} />
                  <Typography variant="caption">{progressImage}%</Typography>
                </Box>
              )}

              {errorImage && (
                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="caption" color="error">
                    {errorImage}
                  </Typography>
                  {imageFile && (
                    <Button
                      size="small"
                      startIcon={<ReplayIcon />}
                      onClick={retryImage}
                      disabled={uploading}
                      sx={{ textTransform: "none" }}
                    >
                      Retry this file
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            bgcolor: theme.colors.mediumPurple,
            borderTop: `1px solid ${theme.colors.palePurple}`,
          }}
        >
          <Button
            onClick={handleCloseManual}
            disabled={uploading}
            sx={{
              color: theme.colors.amber,
              fontFamily: "basic-sans",
              "&:hover": { color: theme.colors.green, background: "transparent" },
            }}
          >
            Close
          </Button>
          <Button
            onClick={handleSaveManual}
            disabled={uploading}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "5px",
              backgroundColor: theme.colors.green,
            }}
          >
            {uploading ? "Uploading…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Confirmation */}
      <Dialog
        open={confirmOpen}
        onClose={handleCloseRemoveConfirm}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            backgroundColor: theme.colors.mediumPurple,
            color: "white",
            fontFamily: "basic-sans",
            border: `1px solid ${theme.colors.palePurple}`,
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: theme.colors.headerPurple,
            color: theme.colors.amber,
            fontWeight: 700,
            borderBottom: `1px solid ${theme.colors.palePurple}`,
            pr: 6,
          }}
        >
          Remove selected content?
          <IconButton
            aria-label="close"
            onClick={handleCloseRemoveConfirm}
            size="small"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.colors.amber,
              "&:hover": { color: theme.colors.green },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: theme.colors.palePurple }}>
          <Typography variant="body2">
            {selected.length} item{selected.length !== 1 ? "s" : ""} will be removed from this
            event. Manual assets will also be deleted from storage.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            bgcolor: theme.colors.mediumPurple,
            borderTop: `1px solid ${theme.colors.palePurple}`,
          }}
        >
          <Button
            onClick={handleCloseRemoveConfirm}
            disabled={removing}
            sx={{
              color: theme.colors.amber,
              "&:hover": { color: theme.colors.green, background: "transparent" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveSelected}
            disabled={removing}
            color="error"
            variant="contained"
            sx={{ textTransform: "none", borderRadius: "5px" }}
          >
            {removing ? "Removing…" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          elevation={3}
          sx={{ width: "100%" }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContentList;
